import { useState, useEffect, useCallback, useRef } from "react";
import { api, apiError, formatApiError, uploadFile } from "@/lib/api";
import { useExportDownload } from "@/hooks/useExportDownload";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Upload, Plus, Trash2, FileDown, Copy, Save, FileSpreadsheet, Sparkles, Hammer, Package, Layers, ChevronDown, ChevronRight, Search, List, Loader2, X, AlertTriangle } from "lucide-react";
import { StatCard } from "@/components/shared/PagePrimitives";

const TYPES = ["Práce", "Materiál", "Ostatní"];
const UNITS = ["m2", "m3", "ks", "kg", "t", "hod", "kpl", "bm"];
const FIELDS = [["code", "Kód"], ["description", "Popis"], ["unit", "MJ"], ["quantity", "Množství"], ["unit_price", "JC"], ["total", "Celkem"], ["section", "Oddíl"], ["type", "Typ"]];
const uid = () => Math.random().toString(36).slice(2);

function calcRollups(sections) {
  const byType = { Práce: 0, Materiál: 0, Ostatní: 0 };
  let grand = 0;
  const bySection = {};
  for (const s of sections) {
    let st = 0;
    for (const it of s.items) { const t = Number(it.total) || 0; st += t; grand += t; byType[TYPES.includes(it.type) ? it.type : "Ostatní"] += t; }
    bySection[s.id] = st;
  }
  return { byType, grand, bySection };
}

export function ProjectBudget({ projectId }) {
  const [sections, setSections] = useState([]);
  const [hasBudget, setHasBudget] = useState(false);
  const [planned, setPlanned] = useState(0);
  const [drawdown, setDrawdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [imp, setImp] = useState(null); // {mode, columns, rows, mapping, header_row, file_url, busy}
  const [dupOpen, setDupOpen] = useState(false);
  const [dupSrc, setDupSrc] = useState("");
  const fileRef = useRef(null);
  // Velký rozpočet z KROSu má klidně 60 oddílů a 600 položek. Vykreslit je
  // najednou znamená tisíce inputů — proto jsou oddíly ve výchozím stavu
  // sbalené (drží se jen ID otevřených), nad nimi je rejstřík pro skok
  // a hledání přes kód i popis.
  const [openIds, setOpenIds] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [indexOpen, setIndexOpen] = useState(false);
  const [contractPrice, setContractPrice] = useState(0);
  const [syncOpen, setSyncOpen] = useState(null);   // {grand, contract} po uložení
  const [markup, setMarkup] = useState("0");
  const [syncing, setSyncing] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPlanned, setDelPlanned] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const sectionRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, pr] = await Promise.all([api.get(`/projects/${projectId}/budget`), api.get("/projects/options")]);
      const secs = data.budget ? data.budget.sections : [];
      setSections(secs);
      // Malý rozpočet (do 5 oddílů) je přehlednější rozbalený, velký sbalený.
      setOpenIds(new Set(secs.length <= 5 ? secs.map((s) => s.id) : []));
      setHasBudget(!!data.budget);
      setPlanned(data.planned_budget || 0);
      setContractPrice(data.contract_price || 0);
      setDrawdown(data.drawdown || null);
      setProjects(pr.data.filter((p) => p.id !== projectId));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const roll = calcRollups(sections);

  const toggleSection = (id) => setOpenIds((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allOpen = sections.length > 0 && sections.every((s) => openIds.has(s.id));
  const toggleAll = () => setOpenIds(allOpen ? new Set() : new Set(sections.map((s) => s.id)));

  // Hledání přes kód i popis. Oddíl s nálezem se rozbalí sám a ostatní zmizí —
  // jinak by se 600 položek muselo hledat očima.
  const needle = q.trim().toLowerCase();
  const matches = (it) => !needle
    || String(it.code || "").toLowerCase().includes(needle)
    || String(it.description || "").toLowerCase().includes(needle);
  const visibleSections = needle
    ? sections.map((s, si) => ({ s, si })).filter(({ s }) => s.items.some(matches))
    : sections.map((s, si) => ({ s, si }));
  const hitCount = needle ? sections.reduce((a, s) => a + s.items.filter(matches).length, 0) : 0;

  const jumpTo = (id) => {
    setOpenIds((prev) => new Set(prev).add(id));
    setIndexOpen(false);
    // Rozbalení proběhne až po překreslení, teprve pak má prvek správnou pozici.
    setTimeout(() => sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const setItem = (si, ii, k, v) => setSections((prev) => {
    const s = prev.map((x) => ({ ...x, items: [...x.items] }));
    const it = { ...s[si].items[ii], [k]: v };
    if (k === "quantity" || k === "unit_price") it.total = Math.round((Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * 100) / 100;
    s[si].items[ii] = it; return s;
  });
  const addItem = (si) => setSections((prev) => { const s = prev.map((x) => ({ ...x, items: [...x.items] })); s[si].items.push({ id: uid(), code: "", description: "", unit: "", quantity: 0, unit_price: 0, total: 0, type: "Práce" }); return s; });
  const removeItem = (si, ii) => setSections((prev) => { const s = prev.map((x) => ({ ...x, items: [...x.items] })); s[si].items.splice(ii, 1); return s; });
  const addSection = () => setSections((prev) => [...prev, { id: uid(), name: "Nový oddíl", items: [] }]);
  const removeSection = (si) => setSections((prev) => prev.filter((_, i) => i !== si));
  const setSectionName = (si, v) => setSections((prev) => prev.map((s, i) => i === si ? { ...s, name: v } : s));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/budget`, { sections, source: hasBudget ? "manual" : "import" });
      setSections(data.budget.sections); setHasBudget(true);
      // Auto-sync: pole „Rozpočet" zakázky nastavil backend na součet položek.
      if (data.planned_budget_synced != null) {
        setPlanned(data.planned_budget_synced);
        toast.success(`Rozpočet uložen. Rozpočet zakázky nastaven na ${formatCZK(data.planned_budget_synced)}.`);
      } else {
        toast.success("Rozpočet uložen.");
      }
      // Cena díla je JINÉ pole než rozpočet: rozpočet = plán nákladů,
      // cena díla = co fakturujeme investorovi (počítá se z ní marže).
      // Když se rozliší o víc než 1 %, nabídneme srovnání — automaticky to
      // přepsat nejde, smluvní cena bývá jiná než součet rozpočtu.
      const grand = data.grand_total || 0;
      const cp = data.contract_price || 0;
      setContractPrice(cp);
      if (grand > 0 && (cp <= 0 || Math.abs(grand - cp) / grand > 0.01)) {
        setMarkup("0");
        setSyncOpen({ grand, contract: cp });
      }
      // 220: čerpání přijde rovnou v odpovědi PUT (dřív se hned potom volal GET /budget znovu).
      if (data.drawdown) setDrawdown(data.drawdown);
      else api.get(`/projects/${projectId}/budget`).then((r) => setDrawdown(r.data.drawdown)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImp({ busy: true, stage: "upload", pct: 0 });
    setImportOpen(true);
    try {
      // onProgress hlásí (hotové kousky, celkem) a jen u souborů nad 512 kB,
      // které jdou po částech. U menších zůstane pct 0 a ukazuje se jen spinner.
      const url = await uploadFile(file, {
        onProgress: (done, total) => setImp((s) => (s ? { ...s, pct: Math.round((done / total) * 100) } : s)),
      });
      setImp((s) => (s ? { ...s, stage: "parse", pct: 100 } : s));
      const { data } = await api.post(`/projects/${projectId}/budget/import/preview`, { file_url: url });
      if (data.mode === "pdf" || data.mode === "kros") {
        setImp({ mode: data.mode, file_url: url, sections: data.sections, busy: false });
      } else {
        setImp({ mode: data.mode, file_url: url, columns: data.columns, rows: data.rows, header_row: data.header_row, mapping: data.suggested_mapping || {}, busy: false });
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import selhal."); setImportOpen(false); setImp(null); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const buildFromMapping = async () => {
    setImp((s) => ({ ...s, busy: true, stage: "build" }));
    try {
      const { data } = await api.post(`/projects/${projectId}/budget/import/build`, { file_url: imp.file_url, mapping: imp.mapping, header_rows: (imp.header_row || 0) + 1, default_type: "Práce" });
      applyImported(data.sections);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setImp((s) => ({ ...s, busy: false })); }
  };
  const applyImported = (secs) => {
    const withIds = secs.map((s) => ({ ...s, id: s.id || uid(), items: s.items.map((it) => ({ ...it, id: it.id || uid() })) }));
    setSections(withIds);
    setOpenIds(new Set(withIds.length <= 5 ? withIds.map((s) => s.id) : []));
    setQ("");
    setImportOpen(false); setImp(null);
    const n = secs.reduce((a, s) => a + s.items.length, 0);
    toast.success(`Načteno ${secs.length} oddílů a ${n} položek. Zkontrolujte a uložte.`);
  };

  const doSyncContractPrice = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/budget/sync-contract-price`, { markup_pct: Number(markup) || 0 });
      setContractPrice(data.contract_price);
      setSyncOpen(null);
      toast.success(`Cena díla nastavena na ${formatCZK(data.contract_price)}.`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  const doDuplicate = async () => {
    if (!dupSrc) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/budget/duplicate`, { source_project_id: dupSrc });
      applyImported(data.sections); setDupOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteBudget = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/projects/${projectId}/budget?reset_planned=${delPlanned ? "true" : "false"}`);
      setSections([]); setHasBudget(false); setOpenIds(new Set()); setQ("");
      setDrawdown(null);
      if (data.reset_planned) setPlanned(0);
      setDelOpen(false);
      toast.success(`Rozpočet smazán (${data.sections} oddílů, ${data.items} položek).`);
    } catch (e) {
      // 404 = rozpočet už na serveru není (smazal ho kolega, nebo prošel
      // předchozí pokus). Cíl je splněný — hlásit chybu by jen mátlo.
      if (e.response?.status === 404) {
        setSections([]); setHasBudget(false); setOpenIds(new Set()); setQ("");
        setDrawdown(null); setDelOpen(false);
        toast.success("Rozpočet je smazaný.");
      } else {
        // apiError místo formatApiError: u chyby serveru bez pole `detail`
        // řekne aspoň „Chyba serveru (500)" místo obecného „Došlo k chybě".
        toast.error(apiError(e));
      }
    }
    finally { setDeleting(false); }
  };

  // 217: export běží jako stažení blobu — tlačítka jsou mezitím zakázaná, druhý klik nic nespustí.
  const { busy: exporting, run: runExport } = useExportDownload();
  const exportBudget = (fmt) => runExport(`budget-${fmt}`, `/api/projects/${projectId}/budget/export?fmt=${fmt}`, `rozpocet.${fmt}`, "Připravuji export rozpočtu…");
  const exportDrawdown = (fmt) => runExport(`drawdown-${fmt}`, `/api/projects/${projectId}/budget/drawdown/export?fmt=${fmt}`, `cerpani_rozpoctu.${fmt}`, "Připravuji export čerpání…");
  const exportIcon = (key, Icon, size) => (exporting === key
    ? <Loader2 size={size} className="animate-spin motion-reduce:animate-none" /> : <Icon size={size} />);

  if (loading) return <div className="bg-card border border-border rounded-[20px] p-8 text-center text-muted-foreground">Načítám…</div>;

  return (
    <div className="space-y-4" data-testid="project-budget">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2 bg-card border border-border rounded-[20px] p-3">
        <input ref={fileRef} type="file" accept=".xlsx,.csv,.pdf,.xml" onChange={onFile} className="hidden" data-testid="budget-file-input" />
        <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="budget-import-btn"><Upload size={16} /> Import (XLSX/CSV/PDF/XML)</Button>
        <Button variant="outline" onClick={() => setDupOpen(true)} data-testid="budget-duplicate-btn"><Copy size={16} /> Duplikovat z jiné zakázky</Button>
        <Button variant="outline" onClick={addSection} data-testid="budget-add-section"><Plus size={16} /> Přidat oddíl</Button>
        <div className="flex-1" />
        {hasBudget && <Button variant="outline" onClick={() => exportBudget("csv")} disabled={!!exporting} data-testid="budget-export-csv">{exportIcon("budget-csv", FileDown, 16)} CSV</Button>}
        {hasBudget && <Button variant="outline" onClick={() => exportBudget("xlsx")} disabled={!!exporting} data-testid="budget-export-xlsx">{exportIcon("budget-xlsx", FileSpreadsheet, 16)} XLSX</Button>}
        {hasBudget && (
          <Button variant="outline" onClick={() => { setDelPlanned(true); setDelOpen(true); }} data-testid="budget-delete-btn"
            className="text-destructive hover:text-destructive hover:border-destructive">
            <Trash2 size={16} /> Smazat rozpočet
          </Button>
        )}
      </div>

      {sections.length === 0 ? (
        hasBudget ? (
          /* Smazal všechny oddíly, ale NEULOŽIL. Dřív se tu ukázalo „Zakázka
             zatím nemá rozpočet." a zmizelo tlačítko Uložit — změna se tak
             nedala potvrdit a po přepnutí záložky se rozpočet vrátil ze serveru.
             Teď je jasně vidět, že jde o neuloženou změnu, a jsou tu obě cesty. */
          <div className="bg-card border border-amber-500/40 rounded-[20px] p-6 md:p-8 text-center" data-testid="budget-empty-unsaved">
            <AlertTriangle size={36} className="mx-auto text-amber-500 mb-3" />
            <p className="font-bold">Smazali jste všechny oddíly — zatím jen tady v prohlížeči.</p>
            <p className="text-muted-foreground text-sm mt-1 max-w-[520px] mx-auto">
              Na serveru rozpočet pořád je. Dokud změnu nepotvrdíte, po přepnutí záložky se vrátí.
            </p>
            <div className="flex flex-col md:flex-row md:justify-center gap-2 mt-5">
              <Button variant="outline" onClick={load} className="h-11 w-full md:w-auto font-semibold" data-testid="budget-empty-restore">
                Vrátit rozpočet zpět
              </Button>
              <Button onClick={() => { setDelPlanned(true); setDelOpen(true); }} data-testid="budget-empty-delete"
                className="h-11 w-full md:w-auto font-bold bg-destructive text-destructive-foreground hover:bg-destructive/90">
                <Trash2 size={16} /> Smazat rozpočet natrvalo
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-[20px] p-8 text-center" data-testid="budget-empty">
            <Calculator size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="font-bold">Zakázka zatím nemá rozpočet.</p>
            <p className="text-muted-foreground text-sm mt-1">Importujte z XLSX/CSV/PDF, duplikujte z jiné zakázky, nebo přidejte oddíl ručně.</p>
          </div>
        )
      ) : (
        <>
          {/* summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="budget-summary">
            <StatCard icon={Hammer} label="Práce" value={formatCZK(roll.byType["Práce"])} />
            <StatCard icon={Package} label="Materiál" value={formatCZK(roll.byType["Materiál"])} />
            <StatCard icon={Layers} label="Ostatní" value={formatCZK(roll.byType["Ostatní"])} />
            <StatCard icon={Calculator} label="Celkem rozpočet" value={formatCZK(roll.grand)} brand testid="budget-grand-total" />
          </div>

          {/* Čerpání rozpočtu */}
          {drawdown && (
            <div className="bg-card border border-border rounded-[20px] p-4" data-testid="budget-drawdown">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <h3 className="font-bold">Čerpání rozpočtu</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => exportDrawdown("csv")} disabled={!!exporting} data-testid="drawdown-export-csv">{exportIcon("drawdown-csv", FileDown, 14)} CSV</Button>
                  <Button variant="outline" size="sm" onClick={() => exportDrawdown("xlsx")} disabled={!!exporting} data-testid="drawdown-export-xlsx">{exportIcon("drawdown-xlsx", FileSpreadsheet, 14)} XLSX</Button>
                </div>
              </div>
              <div className="space-y-3">
                {drawdown.by_type.map((r) => (
                  <div key={r.type} data-testid={`drawdown-${r.type}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-semibold">{r.type}</span>
                      <span className={r.over ? "text-destructive font-bold" : "text-muted-foreground"}>
                        {formatCZK(r.actual)} / {formatCZK(r.plan)} · <b className={r.over ? "text-destructive" : "text-foreground"}>{r.pct}%</b>
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div className={`h-full ${r.over ? "bg-destructive" : "bg-brand"}`} style={{ width: `${Math.min(r.pct, 100)}%` }} />
                    </div>
                    <div className={`text-xs mt-0.5 ${r.remaining < 0 ? "text-destructive" : "text-muted-foreground"}`}>Zbývá: {formatCZK(r.remaining)}</div>
                  </div>
                ))}
                <div className="border-t border-border pt-3" data-testid="drawdown-grand">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-black uppercase">Celkem</span>
                    <span className={drawdown.grand.over ? "text-destructive font-bold" : "font-bold"}>
                      {formatCZK(drawdown.grand.actual)} / {formatCZK(drawdown.grand.plan)} · {drawdown.grand.pct}%
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full ${drawdown.grand.over ? "bg-destructive" : "bg-brand"}`} style={{ width: `${Math.min(drawdown.grand.pct, 100)}%` }} />
                  </div>
                  <div className={`text-xs mt-0.5 ${drawdown.grand.remaining < 0 ? "text-destructive" : "text-muted-foreground"}`}>Zbývá: {formatCZK(drawdown.grand.remaining)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Navigace nad rozpočtem: hledání, rejstřík oddílů a hromadné
              rozbalení. U šesti oddílů zbytečnost, u šedesáti nutnost. */}
          <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border border-border rounded-[16px] p-2.5 flex flex-wrap items-center gap-2" data-testid="budget-nav">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat v položkách (kód nebo popis)…"
                className="h-10 pl-9 pr-8 w-full min-w-0" data-testid="budget-search" />
              {q && (
                <button type="button" onClick={() => setQ("")} data-testid="budget-search-clear"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X size={15} /></button>
              )}
            </div>
            <Button variant="outline" onClick={() => setIndexOpen(true)} className="h-10 w-full md:w-auto font-semibold" data-testid="budget-index-btn">
              <List size={15} /> Rejstřík oddílů ({sections.length})
            </Button>
            <Button variant="outline" onClick={toggleAll} className="h-10 w-full md:w-auto font-semibold" data-testid="budget-toggle-all">
              {allOpen ? <ChevronRight size={15} /> : <ChevronDown size={15} />} {allOpen ? "Sbalit vše" : "Rozbalit vše"}
            </Button>
          </div>

          {needle && (
            hitCount > 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="budget-search-info">
                Nalezeno <b className="text-foreground">{hitCount}</b> položek v {visibleSections.length} oddílech.
              </p>
            ) : (
              <div className="bg-card border border-border rounded-[20px] p-6 text-center" data-testid="budget-search-empty">
                <p className="font-bold">Nic jsme nenašli.</p>
                <p className="text-sm text-muted-foreground mt-1">Hledá se v kódu i popisu položky. Zkuste kratší výraz, třeba „beton" místo celého názvu.</p>
              </div>
            )
          )}

          {visibleSections.map(({ s, si }) => {
            const open = openIds.has(s.id) || !!needle;
            const items = needle ? s.items.map((it, ii) => ({ it, ii })).filter(({ it }) => matches(it))
                                 : s.items.map((it, ii) => ({ it, ii }));
            return (
            <div key={s.id} ref={(el) => { sectionRefs.current[s.id] = el; }}
              className="bg-card border border-border rounded-[20px] p-4 scroll-mt-24" data-testid={`budget-section-${si}`}>
              <div className="flex items-center gap-2 mb-3">
                <button type="button" onClick={() => toggleSection(s.id)} data-testid={`budget-section-toggle-${si}`}
                  className="shrink-0 text-muted-foreground hover:text-brand" title={open ? "Sbalit oddíl" : "Rozbalit oddíl"}>
                  {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                <Input value={s.name} onChange={(e) => setSectionName(si, e.target.value)} className="h-9 font-bold flex-1" data-testid={`budget-section-name-${si}`} />
                <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">{s.items.length} pol.</span>
                <span className="font-bold text-brand shrink-0">{formatCZK(roll.bySection[s.id] || 0)}</span>
                <button onClick={() => removeSection(si)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`budget-remove-section-${si}`}><Trash2 size={16} /></button>
              </div>
              {open && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-1 pr-2 w-24">Kód</th><th className="py-1 pr-2">Popis</th><th className="py-1 pr-2 w-20">MJ</th>
                    <th className="py-1 pr-2 w-24">Množ.</th><th className="py-1 pr-2 w-28">JC</th><th className="py-1 pr-2 w-28">Celkem</th>
                    <th className="py-1 pr-2 w-32">Typ</th><th className="w-8"></th>
                  </tr></thead>
                  <tbody>
                    {items.map(({ it, ii }) => (
                      <tr key={it.id} className="border-b border-border/50" data-testid={`budget-item-${si}-${ii}`}>
                        <td className="py-1 pr-1"><Input value={it.code || ""} onChange={(e) => setItem(si, ii, "code", e.target.value)} className="h-8 text-xs" /></td>
                        <td className="py-1 pr-1"><Input value={it.description || ""} onChange={(e) => setItem(si, ii, "description", e.target.value)} className="h-8" /></td>
                        <td className="py-1 pr-1">
                          <Select value={it.unit || ""} onValueChange={(v) => setItem(si, ii, "unit", v)}>
                            <SelectTrigger className="h-8"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-1 pr-1"><Input type="number" value={it.quantity} onChange={(e) => setItem(si, ii, "quantity", e.target.value)} className="h-8" /></td>
                        <td className="py-1 pr-1"><Input type="number" value={it.unit_price} onChange={(e) => setItem(si, ii, "unit_price", e.target.value)} className="h-8" /></td>
                        <td className="py-1 pr-1 font-semibold tabular-nums">{formatCZK(it.total)}</td>
                        <td className="py-1 pr-1">
                          <Select value={it.type} onValueChange={(v) => setItem(si, ii, "type", v)}>
                            <SelectTrigger className="h-8" data-testid={`budget-item-type-${si}-${ii}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td><button onClick={() => removeItem(si, ii)} className="text-muted-foreground hover:text-destructive" data-testid={`budget-remove-item-${si}-${ii}`}><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {open && (
                <Button variant="ghost" size="sm" onClick={() => addItem(si)} className="text-brand hover:text-brand mt-2" data-testid={`budget-add-item-${si}`}><Plus size={14} /> Přidat položku</Button>
              )}
            </div>
            );
          })}

          <div className="flex items-center justify-between gap-3 sticky bottom-0 bg-background/80 backdrop-blur py-2">
            <span className="text-sm text-muted-foreground min-w-0 truncate">
              Plán zakázky: <b className="text-foreground">{formatCZK(planned)}</b>
              {contractPrice > 0 && <span className="hidden sm:inline"> · Cena díla: <b className="text-foreground">{formatCZK(contractPrice)}</b></span>}
            </span>
            <Button onClick={() => save()} disabled={saving} data-testid="budget-save" className="h-11 font-bold px-6"><Save size={16} /> {saving ? "Ukládám…" : "Uložit rozpočet"}</Button>
          </div>
        </>
      )}

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { if (!o) { setImportOpen(false); setImp(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="budget-import-dialog">
          <DialogHeader><DialogTitle>Import rozpočtu</DialogTitle></DialogHeader>
          {imp?.busy && (
            <div className="py-8 flex flex-col items-center gap-3" data-testid="budget-import-busy">
              <Loader2 size={28} className="text-brand animate-spin motion-reduce:animate-none" />
              <p className="font-bold">
                {imp.stage === "upload" ? "Nahrávám soubor…" : imp.stage === "build" ? "Připravuji položky…" : "Čtu rozpočet…"}
              </p>
              <p className="text-[13px] text-muted-foreground text-center max-w-[380px]">
                {imp.stage === "upload" ? "U velkého rozpočtu to chvíli trvá — okno nezavírejte."
                  : imp.stage === "build" ? "Skládám oddíly a položky do editoru."
                  : "Hledám v souboru soupisy prací a položky. U rozsáhlé stavby to může trvat i půl minuty."}
              </p>
              {imp.stage === "upload" && imp.pct > 0 && (
                <div className="w-full max-w-[380px] h-2 rounded-full bg-secondary overflow-hidden" data-testid="budget-import-progress">
                  <div className="h-full bg-brand transition-all" style={{ width: `${Math.min(imp.pct, 100)}%` }} />
                </div>
              )}
            </div>
          )}
          {imp && !imp.busy && (imp.mode === "pdf" || imp.mode === "kros") && (
            <div className="space-y-3">
              {imp.mode === "kros" ? (
                <div className="flex items-center gap-2 text-sm bg-success/10 border border-success/30 rounded-md p-3" data-testid="budget-import-kros-banner">
                  <FileSpreadsheet size={16} className="text-success shrink-0" /> Rozpoznán export z KROS/ÚRS — položky načteny přesně podle soupisů a dílů, výměry a součty vynechány.
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm bg-brand/10 border border-brand/30 rounded-md p-3"><Sparkles size={16} className="text-brand" /> Rozpočet přečten z PDF pomocí AI — <b>před uložením prosím zkontrolujte</b> položky a ceny.</div>
              )}
              <p className="text-sm">Nalezeno oddílů: <b>{imp.sections.length}</b>, položek: <b>{imp.sections.reduce((a, s) => a + s.items.length, 0)}</b>.</p>
              <Button onClick={() => applyImported(imp.sections)} className="w-full h-11 font-bold" data-testid="budget-import-apply-pdf">Načíst do editoru</Button>
            </div>
          )}
          {imp && !imp.busy && imp.mode !== "pdf" && imp.mode !== "kros" && imp.columns && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Přiřaďte sloupce ze souboru k polím rozpočtu.</p>
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map(([f, lbl]) => (
                  <div key={f}>
                    <Label className="text-xs">{lbl}</Label>
                    <Select value={imp.mapping[f] != null ? String(imp.mapping[f]) : "none"} onValueChange={(v) => setImp((s) => ({ ...s, mapping: { ...s.mapping, [f]: v === "none" ? undefined : Number(v) } }))}>
                      <SelectTrigger className="h-9 mt-1" data-testid={`budget-map-${f}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— nemapovat —</SelectItem>
                        {imp.columns.map((c) => <SelectItem key={c.index} value={String(c.index)}>{c.header || `Sloupec ${c.index + 1}`}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="border border-border rounded-md overflow-x-auto max-h-52">
                <table className="text-xs w-full">
                  <tbody>
                    {imp.rows.slice(0, 10).map((r, i) => (
                      <tr key={i} className="border-b border-border/50">{r.map((c, j) => <td key={j} className="px-2 py-1 whitespace-nowrap max-w-[160px] truncate">{c}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button onClick={buildFromMapping} className="w-full h-11 font-bold" data-testid="budget-import-build">Načíst do editoru</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejstřík oddílů — skok na oddíl bez scrollování celého rozpočtu */}
      <Dialog open={indexOpen} onOpenChange={setIndexOpen}>
        <DialogContent className="max-w-lg" data-testid="budget-index-dialog">
          <DialogHeader><DialogTitle>Rejstřík oddílů</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {sections.map((s, si) => (
              <button key={s.id} type="button" onClick={() => jumpTo(s.id)} data-testid={`budget-index-item-${si}`}
                className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-[12px] hover:bg-secondary transition-colors border-b border-border/50 last:border-0">
                <span className="flex-1 min-w-0 truncate text-sm font-semibold">{s.name || "Bez názvu"}</span>
                <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{s.items.length} pol.</span>
                <span className="text-sm font-bold text-brand shrink-0 tabular-nums whitespace-nowrap">{formatCZK(roll.bySection[s.id] || 0)}</span>
              </button>
            ))}
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-border text-sm">
            <span className="text-muted-foreground">Celkem {sections.length} oddílů</span>
            <b className="text-brand">{formatCZK(roll.grand)}</b>
          </div>
        </DialogContent>
      </Dialog>

      {/* Srovnání ceny díla s rozpočtem. Rozpočet = plán nákladů, cena díla =
          co fakturujeme investorovi; u importu z ÚRS/KROSu bývá rozpočet
          rovnou nabídkovou cenou, proto ta nabídka. */}
      <Dialog open={!!syncOpen} onOpenChange={(o) => { if (!o) setSyncOpen(null); }}>
        <DialogContent data-testid="budget-sync-price-dialog">
          <DialogHeader><DialogTitle>Srovnat cenu díla s rozpočtem?</DialogTitle></DialogHeader>
          {syncOpen && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary/60 border border-border rounded-[14px] p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Rozpočet</div>
                  <div className="text-lg font-black tabular-nums mt-0.5" data-testid="sync-grand">{formatCZK(syncOpen.grand)}</div>
                </div>
                <div className="bg-secondary/60 border border-border rounded-[14px] p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Cena díla nyní</div>
                  <div className="text-lg font-black tabular-nums mt-0.5" data-testid="sync-contract">{syncOpen.contract > 0 ? formatCZK(syncOpen.contract) : "nezadána"}</div>
                </div>
              </div>
              <p className="text-[13px] text-muted-foreground">
                Cena díla je částka pro investora — počítá se z ní marže. Rozpočet je plán nákladů.
                Když je rozpočet zároveň vaší nabídkou (typicky import z ÚRS/KROSu), srovnejte je.
                Pracujete-li s odhadem, nechte cenu díla beze změny.
              </p>
              <div>
                <Label className="text-xs">Připočíst k rozpočtu přirážku (%)</Label>
                <Input type="number" min="0" max="500" step="0.5" value={markup} onChange={(e) => setMarkup(e.target.value)}
                  className="h-11 mt-1 w-full min-w-0 tabular-nums" data-testid="sync-markup" />
                <p className="text-[13px] text-muted-foreground mt-1.5">
                  Cena díla bude <b className="text-foreground tabular-nums">{formatCZK(Math.round(syncOpen.grand * (1 + (Number(markup) || 0) / 100)))}</b>
                  {(Number(markup) || 0) > 0 ? ` (rozpočet + ${Number(markup)} %)` : " (přesně součet rozpočtu)"}.
                </p>
              </div>
              <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
                <Button variant="outline" onClick={() => setSyncOpen(null)} className="h-11 w-full md:w-auto" data-testid="sync-keep">Ponechat</Button>
                <Button onClick={doSyncContractPrice} disabled={syncing} className="h-11 w-full md:w-auto font-bold" data-testid="sync-apply">
                  {syncing ? "Ukládám…" : "Nastavit cenu díla"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Smazání celého rozpočtu */}
      <Dialog open={delOpen} onOpenChange={(o) => { if (!o) setDelOpen(false); }}>
        <DialogContent data-testid="budget-delete-dialog">
          <DialogHeader><DialogTitle>Smazat celý rozpočet?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              {sections.length > 0 ? (
                <>Smaže se <b>{sections.length} oddílů</b> a <b>{sections.reduce((a, s) => a + s.items.length, 0)} položek</b> za <b>{formatCZK(roll.grand)}</b>.</>
              ) : (
                <>Smaže se celý rozpočet uložený na serveru.</>
              )}
              {" "}Tuhle akci nelze vrátit — rozpočet bude potřeba nahrát nebo zadat znovu.
            </p>
            <div className="rounded-[12px] bg-secondary/60 border border-border p-3 text-[13px] text-muted-foreground">
              <b className="text-foreground">Co zůstane:</b> faktury, účtenky, docházka i skutečné náklady zakázky.
              Mizí jen plán, se kterým se porovnávaly — čerpání rozpočtu proto přestane mít s čím počítat.
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={delPlanned} onChange={(e) => setDelPlanned(e.target.checked)}
                data-testid="budget-delete-reset-planned"
                className="w-5 h-5 mt-0.5 shrink-0 accent-[hsl(var(--accent-orange))] cursor-pointer" />
              <span>
                <span className="block text-[15px] font-semibold">Vynulovat i pole Rozpočet na zakázce</span>
                <span className="block text-[13px] text-muted-foreground mt-0.5 leading-snug">
                  Teď je {formatCZK(planned)} — nastavilo se automaticky z tohoto rozpočtu. Cena díla se nemění.
                </span>
              </span>
            </label>
            <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
              <Button variant="outline" onClick={() => setDelOpen(false)} className="h-11 w-full md:w-auto" data-testid="budget-delete-cancel">Zrušit</Button>
              <Button onClick={deleteBudget} disabled={deleting} data-testid="budget-delete-confirm"
                className="h-11 w-full md:w-auto font-bold bg-destructive text-destructive-foreground hover:bg-destructive/90">
                <Trash2 size={16} /> {deleting ? "Mažu…" : "Smazat rozpočet"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate dialog */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent  data-testid="budget-duplicate-dialog">
          <DialogHeader><DialogTitle>Duplikovat rozpočet z jiné zakázky</DialogTitle></DialogHeader>
          <Select value={dupSrc} onValueChange={setDupSrc}>
            <SelectTrigger className="h-11" data-testid="budget-dup-source"><SelectValue placeholder="Vyberte zakázku" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={doDuplicate} disabled={!dupSrc} className="w-full h-11 font-bold" data-testid="budget-dup-confirm">Načíst jako šablonu</Button>
        </DialogContent>
      </Dialog>

    </div>
  );
}
